import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import {
    AttachmentsDef,
    getCommonFilterDefs,
    getCommonTableColumnsDefs,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TCellValue } from "@components/table";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrAnnualTaxSettlementStatusEntity,
    IPrEmploymentEntity,
    IPrPayslipEntity,
    PrAnnualTaxItemTypeEntity,
    PrAnnualTaxSettlementEntity,
    PrAnnualTaxSettlementItemEntity,
    PrAnnualTaxSettlementRequestEntity,
    PrEmploymentEntity,
    PrEmploymentTypeEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import { IFormatOptions } from "@odata/OData.utils";
import {
    AnnualTaxGroupType,
    annualTaxSettlementStatusFormatter,
    getAnnualTaxRequestStatusColor,
    PRINT_PDF_ACTION
} from "@pages/payroll/annualTaxSettlement/AnnualTaxSettlement.utils";
import AnnualTaxSettlementFormView from "@pages/payroll/annualTaxSettlement/AnnualTaxSettlementFormView";
import AnnualTaxSettlementOverview from "@pages/payroll/annualTaxSettlement/overView/AnnualTaxSettlementOverview";
import {
    ATSRResultFormatter,
    ATSRStatusFormatter
} from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.def";
import {
    getEmployeeFilterDef,
    getEmployeeSummaryItemDef,
    getEmployeeTableColumnDefs
} from "@pages/payroll/employee/Employee.utils";
import { getPayslipEmploymentColumnDefinition } from "@pages/payroll/payslip/Payslip.def";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { universalPersonNameFormatter } from "@utils/string";
import i18next from "i18next";
import React from "react";

import { FieldType, Sort } from "../../../enums";
import { TValue } from "../../../global.types";
import { ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST, ROUTE_PAYSLIP } from "../../../routes";
import CurrencyType from "../../../types/Currency";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { IDefinition, IGetDefinition } from "../../PageUtils";

export const taxBaseOverViewPath = BindingContext.localContext("TaxBaseOverviewPath");
export const nontaxableAmountOverviewPath = BindingContext.localContext("NonTaxableAmountOverviewPath");
export const taxOverviewPath = BindingContext.localContext("TaxOverviewPath");
export const benefitOverviewPath = BindingContext.localContext("BenefitOverviewPath");
export const compensationOverviewPath = BindingContext.localContext("CompensationOverviewPath");

// TODO: keep for now, not sure so many empty rows will last
// function isGroupVisible(type: AnnualTaxGroupType): TGetValueFn<boolean> {
//     return (args) => {
//         const items = args.storage.getEntity<IPrAnnualTaxSettlementEntity>()?.Items;
//         if (!items || items.length === 0) {
//             return false;
//         }
//         return getRowDefByGroupType(type).some(rowDef => {
//             return items.some(i => i.TypeCode === rowDef.id);
//         });
//     };
// }

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrAnnualTaxSettlement}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrAnnualTaxSettlementEntity.NumberOurs,
                PrAnnualTaxSettlementEntity.Employee,
                PrAnnualTaxSettlementEntity.Status
            ],
            filterDefinition: {
                ...getCommonFilterDefs(),
                ...getEmployeeFilterDef(PrAnnualTaxSettlementEntity.Employee),
                [PrAnnualTaxSettlementEntity.NumberOurs]: {},
                ...withDisplayName(PrAnnualTaxSettlementEntity.Status)
            },
            isValueHelp: true
        }],
        initialSortBy: [{
            id: PrAnnualTaxSettlementEntity.NumberOurs,
            sort: Sort.Desc
        }],
        additionalProperties: [],
        columns: [
            PrAnnualTaxSettlementEntity.NumberOurs,
            PrAnnualTaxSettlementEntity.Employee,
            PrAnnualTaxSettlementEntity.Status
        ],
        columnDefinition: {
            ...getCommonTableColumnsDefs(),
            [PrAnnualTaxSettlementEntity.NumberOurs]: {},
            ...getEmployeeTableColumnDefs(PrAnnualTaxSettlementEntity.Employee),
            [PrAnnualTaxSettlementEntity.Status]: {
                formatter: annualTaxSettlementStatusFormatter
            }
        },
        title: i18next.t("AnnualTaxSettlement:Table.Title"),
        tabsSettings: {}
    };

    const summary: ISummaryItem[] = [
        { id: PrAnnualTaxSettlementEntity.NumberOurs },
        getEmployeeSummaryItemDef(PrAnnualTaxSettlementEntity.Employee),
        {
            id: PrAnnualTaxSettlementEntity.Status,
            label: i18next.t("AnnualTaxSettlement:Form.Status"),
            formatter: (val: TValue) => {
                const status = val as IPrAnnualTaxSettlementStatusEntity;
                return status?.Name;
            },
            colorFormatter: (val: TValue) => {
                const status = val as IPrAnnualTaxSettlementStatusEntity;
                return getAnnualTaxRequestStatusColor(status?.Code);
            }
        }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.PrAnnualTaxSettlement}Form`,
        translationFiles: getDefinitions.translationFiles,
        title: i18next.t("AnnualTaxSettlement:Form.Title"),
        getItemBreadCrumbText: (storage) => {
            return `${i18next.t("AnnualTaxSettlement:Form.Title")} ${universalPersonNameFormatter(storage.data.entity.Employee)}`;
        },
        isDeletable: false,
        summary,
        customHeaderActions: [{
            id: PRINT_PDF_ACTION,
            label: i18next.t("AnnualTaxSettlement:Form.Print"),
            iconName: "ExportPdf",
            isDisabled: false
        }],
        formControl: AnnualTaxSettlementFormView,
        files: {
            ...AttachmentsDef,
            enabledInReadOnlyMode: true
        },
        additionalProperties: [
            { id: PrAnnualTaxSettlementEntity.Year },
            {
                id: PrAnnualTaxSettlementEntity.Items,
                additionalProperties: [
                    { id: PrAnnualTaxSettlementItemEntity.DecimalValue },
                    { id: PrAnnualTaxSettlementItemEntity.IntValue },
                    { id: PrAnnualTaxSettlementItemEntity.TypeCode },
                    { id: createPath(PrAnnualTaxSettlementItemEntity.Type, PrAnnualTaxItemTypeEntity.Name) }
                ]
            },
        ],
        fieldDefinition: {
            [taxBaseOverViewPath]: {
                type: FieldType.Custom,
                render: ({ storage }) => {
                    return <AnnualTaxSettlementOverview
                        storage={storage as FormStorage<IPrPayslipEntity>}
                        type={AnnualTaxGroupType.TaxBase}
                    />;
                }
            },
            [nontaxableAmountOverviewPath]: {
                type: FieldType.Custom,
                render: ({ storage }) => {
                    return <AnnualTaxSettlementOverview
                        storage={storage as FormStorage<IPrPayslipEntity>}
                        type={AnnualTaxGroupType.NonTaxable}
                    />;
                }
            },
            [taxOverviewPath]: {
                type: FieldType.Custom,
                render: ({ storage }) => {
                    return <AnnualTaxSettlementOverview
                        storage={storage as FormStorage<IPrPayslipEntity>}
                        type={AnnualTaxGroupType.Tax}
                    />;
                }
            },
            [benefitOverviewPath]: {
                type: FieldType.Custom,
                render: ({ storage }) => {
                    return <AnnualTaxSettlementOverview
                        storage={storage as FormStorage<IPrPayslipEntity>}
                        type={AnnualTaxGroupType.Benefit}
                    />;
                }
            },
            [compensationOverviewPath]: {
                type: FieldType.Custom,
                render: ({ storage }) => {
                    return <AnnualTaxSettlementOverview
                        storage={storage as FormStorage<IPrPayslipEntity>}
                        type={AnnualTaxGroupType.Compensation}
                    />;
                }
            }
        },
        groups: [
            {
                id: "taxBase",
                title: i18next.t("AnnualTaxSettlement:Form.TaxBase"),
                // isVisible: isGroupVisible(AnnualTaxGroupType.TaxBase),
                rows: [[{ id: taxBaseOverViewPath }]]
            }, {
                id: "nontaxableAmount",
                title: i18next.t("AnnualTaxSettlement:Form.NonTaxableAmount"),
                // isVisible: isGroupVisible(AnnualTaxGroupType.NonTaxable),
                rows: [[{ id: nontaxableAmountOverviewPath }]]
            }, {
                id: "tax",
                title: i18next.t("AnnualTaxSettlement:Form.Tax"),
                // isVisible: isGroupVisible(AnnualTaxGroupType.Tax),
                rows: [[{ id: taxOverviewPath }]]
            }, {
                id: "benefit",
                title: i18next.t("AnnualTaxSettlement:Form.Benefit"),
                // isVisible: isGroupVisible(AnnualTaxGroupType.Benefit),
                rows: [[{ id: benefitOverviewPath }]]
            }, {
                id: "compensation",
                title: i18next.t("AnnualTaxSettlement:Form.Compensation"),
                // isVisible: isGroupVisible(AnnualTaxGroupType.Compensation),
                rows: [[{ id: compensationOverviewPath }]]
            }, {
                id: "tabs",
                tabs: [{
                    id: EntitySetName.PrAnnualTaxSettlementRequests,
                    title: i18next.t("AnnualTaxSettlement:Form.Requests"),
                    table: {
                        id: "requestTable",
                        entitySet: EntitySetName.PrAnnualTaxSettlementRequests,
                        filter: (args: IGetValueArgs) => {
                            return `${PrAnnualTaxSettlementRequestEntity.Employee}/Id eq ${args.storage.data.entity.Employee.Id} AND ${PrAnnualTaxSettlementRequestEntity.Year} eq ${args.storage.data.entity.Year}`;
                        },
                        columns: [
                            {
                                id: PrAnnualTaxSettlementRequestEntity.NumberOurs,
                                formatter: (val: TValue, {
                                    entity,
                                    storage
                                }: IFormatOptions<IPrEmploymentEntity>): TCellValue => {
                                    const { Id } = entity;
                                    return getTableIntentLink(val as string, {
                                        route: `${ROUTE_ANNUAL_TAX_SETTLEMENT_REQUEST}/${Id}`,
                                        context: storage.context,
                                        storage: storage
                                    });
                                }
                            }, {
                                id: PrAnnualTaxSettlementRequestEntity.Result,
                                label: i18next.t("AnnualTaxSettlementRequest:Table.Result"),
                                formatter: ATSRResultFormatter
                            }, {
                                id: PrAnnualTaxSettlementRequestEntity.Status,
                                label: i18next.t("AnnualTaxSettlementRequest:Table.Status"),
                                formatter: ATSRStatusFormatter
                            }, {
                                id: PrAnnualTaxSettlementRequestEntity.DateLastModified,
                                label: i18next.t("AnnualTaxSettlementRequest:Table.DateLastModified")
                            }
                        ]
                    }
                }, {
                    id: EntitySetName.PrPayslips,
                    title: i18next.t("AnnualTaxSettlement:Form.Payslip"),
                    table: {
                        id: "payslipTable",
                        entitySet: EntitySetName.PrPayslips,
                        filter: (args: IGetValueArgs) => {
                            return `${PrPayslipEntity.AnnualTaxSettlement}/Id eq ${args.storage.data.entity.Id}`;
                        },
                        columns: [
                            {
                                id: PrPayslipEntity.Year,
                                label: i18next.t("AnnualTaxSettlement:Form.Number"),
                                additionalProperties: [{ id: PrPayslipEntity.Id }, { id: PrPayslipEntity.Month }],
                                formatter: (val: TValue, {
                                    entity,
                                    storage
                                }: IFormatOptions<IPrPayslipEntity>): TCellValue => {
                                    const { Id, Year, Month } = entity;
                                    return getTableIntentLink(`${Year}${Month}`, {
                                        route: `${ROUTE_PAYSLIP}/${Id}`,
                                        context: storage.context,
                                        storage: storage
                                    });
                                }
                            }, {
                                id: PrPayslipEntity.Employments,
                                ...getPayslipEmploymentColumnDefinition(),
                                additionalProperties: [{
                                    id: `${createPath(PrPayslipEmploymentEntity.Employment, PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name)}`
                                }]
                            }, {
                                id: PrPayslipEntity.AggregatedGrossSalary,
                                formatter: (val: TValue, { storage }): TCellValue => {
                                    return CurrencyType.format(val, { currency: getCompanyCurrency(storage.context) });
                                }
                            }
                        ]
                    }
                }]
            }
        ]
    };
    return {
        entitySet: EntitySetName.PrAnnualTaxSettlements,
        table,
        form
    };
};

getDefinitions.translationFiles = ["AnnualTaxSettlement", "AnnualTaxSettlementRequest", "Payslip", "Common"];
setDefByEntityType(EntityTypeName.PrAnnualTaxSettlement, getDefinitions);